import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GuestLayout from '../components/GuestLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { LogOut, User, Package, Edit2, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-warmgold/10 text-warmgold border-warmgold/30',
  confirmed: 'bg-sage/10 text-sage border-sage/30',
  fulfilled: 'bg-sage/10 text-sage border-sage/30',
  cancelled: 'bg-warm-error/10 text-warm-error border-warm-error/30',
};

export default function AccountPage() {
  const { user, token, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/orders/my`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setOrders(res.data)).catch(console.error);
  }, [token]);

  const saveName = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, { name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => ({ ...prev, name }));
      setEditing(false);
      toast.success('Name updated');
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <GuestLayout>
      <div className="px-6 pt-8 max-w-md mx-auto md:max-w-2xl">
        <h1 data-testid="account-title" className="font-heading text-3xl tracking-tight text-espresso mb-6 animate-fade-in">
          Account
        </h1>

        {/* Profile Card */}
        <div className="bg-white border border-warm-border rounded-2xl p-5 mb-8 animate-fade-in">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-sand rounded-full flex items-center justify-center">
              <User size={24} className="text-espresso" />
            </div>
            <div className="flex-1">
              {editing ? (
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-9 bg-sand border-0 rounded-lg text-sm"
                    placeholder="Your name"
                    autoFocus
                  />
                  <button data-testid="save-name-btn" onClick={saveName} className="text-sage">
                    {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="font-body font-semibold text-espresso">{user?.name || 'Guest'}</h2>
                  <button data-testid="edit-name-btn" onClick={() => setEditing(true)} className="text-text-muted">
                    <Edit2 size={14} />
                  </button>
                </div>
              )}
              <p className="text-text-muted text-sm">{user?.phone}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full border-warm-border text-espresso rounded-full hover:bg-sand"
          >
            <LogOut size={16} className="mr-2" /> Sign Out
          </Button>
        </div>

        {/* Orders */}
        <div className="mb-4 flex items-center gap-2">
          <Package size={18} className="text-espresso" />
          <h2 className="font-heading text-xl text-espresso">My Orders</h2>
        </div>

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-muted">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} data-testid={`order-${order.id}`} className="bg-white border border-warm-border rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-text-muted font-mono">#{order.id.slice(0, 8)}</span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusColors[order.status] || statusColors.pending}`}>
                    {order.status}
                  </span>
                </div>
                <div className="space-y-2 mb-3">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-text-muted">{item.title} x{item.quantity}</span>
                      <span className="text-espresso font-medium">${item.subtotal?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-warm-border pt-3 flex justify-between">
                  <span className="text-xs text-text-muted">
                    {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span className="text-espresso font-bold">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin Link */}
        <div className="mt-12 mb-8 text-center">
          <button
            data-testid="admin-link"
            onClick={() => navigate('/admin/login')}
            className="text-text-muted text-xs hover:text-espresso transition-colors underline underline-offset-4"
          >
            Property Manager Login
          </button>
        </div>
      </div>
    </GuestLayout>
  );
}
